import * as Linking from "expo-linking";
import { Formik } from "formik";
import Text from "@/components/shared/AppText";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { updateEventStripeDetails } from "../http/events";
import {
  fetchStripeConnectLink,
  legacyStripeConnectLink,
} from "../http/stripeLinks";
import { View, Platform, TouchableOpacity, TextInput } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { StripeSchema } from "../screens/Schemas";
import AppActivityIndicator from "@/components/shared/AppActivityIndicator";
import { eventLivePageBase } from "../globalUtils";
import AutoSave from "./AutoSave";
import analytics from "@/analytics";
import { Ionicons } from "@expo/vector-icons";
import Button, {
  ButtonRow,
  ButtonStack,
  CopyButton,
} from "@/components/settings/Button";
import Callout from "@/components/settings/Callout";
import { FieldLabel, Hint } from "@/components/settings/Field";
import { NavRow, RowGroup, ToggleRow } from "@/components/settings/SettingRow";
import {
  SettingsPage,
  SettingsSection,
} from "@/components/settings/SettingsPage";
import { colors, control, radius, text } from "@/components/settings/theme";
import useClipboardCallback from "../hooks/useClipboardCallback";
import tw from "twrnc";
import { Dropdown } from "react-native-element-dropdown";
import { useActionSheet } from "@/components/hooks/useActionSheet";
import useRoomsTicketing from "@/components/rooms/useRoomsTicketing";
import { useTeamAccess } from "@/components/teams/useWorkspace";
import { useEventAuth } from "@/components/teams/useWorkspaceAuth";
import usePayoutAccounts from "@/components/payouts/usePayoutAccounts";
import { accountsToOffer } from "@/components/payouts/choices";
import { SavedAccountChoices } from "@/components/payouts/SellTicketsPayouts";
import { t, tc } from "@/localization";

// What a connected Stripe account gets (shown before connecting).
const TICKET_FEATURES = [
  t("Your Stripe account, your branding, your price"),
  t("Our fee is just US $1.50 per ticket sold (introductory offer)"),
  t("All countries and 31 currencies supported"),
  t("A fixed price, or let buyers choose the price"),
  t("Coupons and other Stripe features"),
];

const dropdownStyle = [
  tw`px-3.5 bg-white`,
  {
    height: control.lg,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: radius.control,
  },
];

const PriceRow = ({
  price,
  index,
  onUpdate,
  onRemove,
  canRemove,
  usedCurrencies = [],
  currenciesData,
}) => {
  // Local state to preserve input format during editing
  const [inputValue, setInputValue] = useState(
    price.amount ? price.amount.toString() : ""
  );
  const [isEditing, setIsEditing] = useState(false);

  // Update local input value when price.amount changes externally (like from currency conversion)
  useEffect(() => {
    if (!isEditing) {
      setInputValue(price.amount ? price.amount.toString() : "");
    }
  }, [price.amount, isEditing]);

  // Update input value when currency changes
  useEffect(() => {
    setInputValue(price.amount ? price.amount.toString() : "");
    setIsEditing(false);
  }, [price.currency]);

  // Known zero-decimal currencies (Stripe standard)
  const knownZeroDecimalCurrencies = [
    "jpy",
    "krw",
    "bif",
    "clp",
    "djf",
    "gnf",
    "kmf",
    "mga",
    "pyg",
    "rwf",
    "ugx",
    "vnd",
    "vuv",
    "xaf",
    "xof",
    "xpf",
  ];

  const isZeroDecimalCurrency = (currencyCode) => {
    const currentCurrency = currenciesData.currencies.find(
      (curr) => curr.code.toLowerCase() === currencyCode
    );
    return (
      currentCurrency?.zeroDecimal ||
      knownZeroDecimalCurrencies.includes(currencyCode?.toLowerCase())
    );
  };

  // Helper function to format the EventLive fee
  const formatEventLiveFee = () => {
    const currentCurrency = getCurrentCurrency();
    if (!currentCurrency || !currentCurrency.fee) {
      return "—";
    }

    const fee = currentCurrency.fee;
    const symbol = currentCurrency.symbol || currentCurrency.code;

    const isZeroDecimal = isZeroDecimalCurrency(price.currency);

    if (isZeroDecimal) {
      return `${symbol}${fee}`;
    } else {
      return `${symbol}${fee.toFixed(2)}`;
    }
  };

  // Return loading state if currencies aren't loaded yet
  if (
    !currenciesData ||
    !currenciesData.currencies ||
    currenciesData.currencies.length === 0
  ) {
    return (
      <View style={tw`flex-row items-end mb-2`}>
        <View style={tw`flex-1 mr-2`}>
          {index === 0 && (
            <Text style={[text.hint, tw`mb-1 font-semibold`]}>
              {t("Currency")}
            </Text>
          )}
          <View
            style={tw`border border-gray-300 rounded-lg p-2.5 h-[45px] bg-gray-100 items-center justify-center`}
          >
            <Text style={text.hint}>{t("Loading currencies…")}</Text>
          </View>
        </View>
        <View style={tw`flex-1 mr-2`}>
          {index === 0 && (
            <Text style={[text.hint, tw`mb-1 font-semibold`]}>
              {t("Amount")}
            </Text>
          )}
          <View
            style={tw`border border-gray-300 rounded-lg p-2.5 h-[45px] bg-gray-100`}
          />
        </View>
        <View style={tw`w-24 mr-2`}>
          {index === 0 && (
            <Text style={[text.hint, tw`mb-1 font-semibold`]}>{t("Fee")}</Text>
          )}
          <View style={tw`h-[45px] items-center justify-center`}>
            <Text style={text.hint}>—</Text>
          </View>
        </View>
        {canRemove && (
          <View style={tw`h-[45px] w-[17px] items-center justify-center`}>
            <View style={tw`w-6 h-6 bg-gray-200 rounded-full`} />
          </View>
        )}
      </View>
    );
  }

  const availableCurrencies = currenciesData.currencies
    .filter(
      (curr) =>
        !usedCurrencies.includes(curr.code.toLowerCase()) ||
        curr.code.toLowerCase() === price.currency
    )
    .map((curr) => ({
      label: curr.exchangeAvailable
        ? `${curr.code} - ${curr.name}`
        : t("{code} - {name} (No auto-conversion)", {
            code: curr.code,
            name: curr.name,
          }),
      value: curr.code.toLowerCase(),
      zeroDecimal: curr.zeroDecimal,
      exchangeAvailable: curr.exchangeAvailable,
    }));

  const getCurrentCurrency = () => {
    return currenciesData.currencies.find(
      (curr) => curr.code.toLowerCase() === price.currency
    );
  };

  const handleAmountChange = (value) => {
    const isZeroDecimal = isZeroDecimalCurrency(price.currency);
    setIsEditing(true);

    // Handle empty input
    if (!value || value.trim() === "") {
      setInputValue("");
      onUpdate(index, "amount", 0);
      return;
    }

    if (isZeroDecimal) {
      // For zero decimal currencies, only allow integers
      const cleanValue = value.replace(/[^0-9]/g, "");
      setInputValue(cleanValue);
      onUpdate(index, "amount", parseInt(cleanValue) || 0);
    } else {
      // For regular currencies, allow decimals with max 2 decimal places
      // First, remove any non-numeric characters except decimal point
      let cleanValue = value.replace(/[^0-9.]/g, "");

      // Handle multiple decimal points - keep only the first one
      const decimalCount = (cleanValue.match(/\./g) || []).length;
      if (decimalCount > 1) {
        const parts = cleanValue.split(".");
        cleanValue = parts[0] + "." + parts.slice(1).join("");
      }

      // Ensure max 2 decimal places
      const parts = cleanValue.split(".");
      let formattedValue = parts[0];
      if (parts.length > 1) {
        formattedValue += "." + parts[1].substring(0, 2);
      }

      // Handle edge cases for display
      if (formattedValue === ".") {
        formattedValue = "0.";
      }
      if (formattedValue.startsWith(".")) {
        formattedValue = "0" + formattedValue;
      }

      // Prevent leading zeros (except for decimal values like 0.5)
      if (
        formattedValue.length > 1 &&
        formattedValue.startsWith("0") &&
        !formattedValue.startsWith("0.")
      ) {
        formattedValue = formattedValue.substring(1);
      }

      // Update input value to preserve formatting during typing
      setInputValue(formattedValue);

      // Only convert to number if it's a complete number (not ending with decimal)
      const finalAmount = formattedValue.endsWith(".")
        ? parseFloat(formattedValue.slice(0, -1)) || 0
        : parseFloat(formattedValue) || 0;

      onUpdate(index, "amount", finalAmount);
    }
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    // Clean up input format on blur
    if (inputValue && !isZeroDecimalCurrency(price.currency)) {
      const cleanValue = inputValue.replace(/\.$/, ""); // Remove trailing decimal
      setInputValue(cleanValue);
      if (cleanValue !== inputValue) {
        onUpdate(index, "amount", parseFloat(cleanValue) || 0);
      }
    }
  };

  const handleInputFocus = () => {
    setIsEditing(true);
  };

  const currentCurrency = getCurrentCurrency();

  return (
    <View style={tw`flex-row items-end mb-2`}>
      <View style={tw`flex-1 mr-2`}>
        {index === 0 && (
          <Text style={[text.hint, tw`mb-1 font-semibold`]}>
            {t("Currency")}
          </Text>
        )}
        <Dropdown
          data={availableCurrencies}
          labelField="label"
          valueField="value"
          value={price.currency || "usd"}
          onChange={(item) => onUpdate(index, "currency", item.value)}
          search
          searchPlaceholder={t("Search currency...")}
          style={tw`border border-gray-300 rounded-lg p-2.5 h-[45px]`}
          selectedTextStyle={text.body}
          itemTextStyle={text.body}
          containerStyle={tw`border border-gray-300 rounded-lg max-h-60`}
          placeholder={t("Currency")}
        />
      </View>
      <View style={tw`flex-1 mr-2`}>
        {index === 0 && (
          <Text style={[text.hint, tw`mb-1 font-semibold`]}>{t("Amount")}</Text>
        )}
        <TextInput
          style={[
            tw`border border-gray-300 rounded-lg px-3 h-[45px] text-[15px]`,
            { color: colors.ink },
          ]}
          value={inputValue}
          onChangeText={handleAmountChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={isZeroDecimalCurrency(price.currency) ? "0" : "0.00"}
          keyboardType={Platform.OS === "ios" ? "decimal-pad" : "numeric"}
          returnKeyType="done"
          autoComplete="off"
          autoCorrect={false}
          spellCheck={false}
        />
      </View>
      <View style={tw`w-16 mr-2`}>
        {index === 0 && (
          <Text style={[text.hint, tw`mb-1 font-semibold`]}>{t("Fee")}</Text>
        )}
        <View style={tw`h-[45px] items-left justify-center`}>
          <Text style={text.body}>{formatEventLiveFee()}</Text>
        </View>
      </View>
      {canRemove && (
        <TouchableOpacity
          onPress={() => onRemove(index)}
          style={tw`h-[45px] w-[17px] items-center justify-center`}
        >
          <Ionicons
            name="remove-circle-outline"
            size={22}
            color={colors.danger}
          />
        </TouchableOpacity>
      )}
    </View>
  );
};

// Isolated exchange-rate lookup for the secondary price lists — deliberately
// does NOT touch the component-level baseCurrency/exchangeRates state that
// drives the main custom_price conversions.
const fetchRatesForBase = async (baseCurrency) => {
  try {
    const response = await fetch(
      `https://evt.live/api/ppvpricing?base_currency=${baseCurrency}`
    );
    const data = await response.json();
    return data.exchangeRates || {};
  } catch (err) {
    console.error("Failed to fetch exchange rates:", err);
    return {};
  }
};

const convertFromBase = (amount, fromCurrency, toCurrency, rates) => {
  if (!amount || fromCurrency === toCurrency) return amount || 0;
  const rate = rates[toCurrency.toUpperCase()];
  return rate ? Math.round(amount * rate * 100) / 100 : 0;
};

// Compact multi-currency price list for the add-on pricing fields
// (per-day, download, replay). Reuses PriceRow. The first row acts as the
// base: added currencies and currency switches auto-convert from it, and
// editing the first amount recalculates the other rows (same behavior as
// the main Custom Prices list).
const SimplePriceList = ({ f, field, currenciesData }) => {
  const prices = f.values[field] || [];

  const currencyInfo = (code) =>
    currenciesData?.currencies?.find(
      (curr) => curr.code.toLowerCase() === (code || "").toLowerCase()
    );

  const canConvert = (fromCode, toCode) =>
    currencyInfo(fromCode)?.exchangeAvailable &&
    currencyInfo(toCode)?.exchangeAvailable;

  return (
    <>
      {prices.map((price, index) => (
        <PriceRow
          key={index}
          price={price}
          index={index}
          onUpdate={async (idx, priceField, value) => {
            const newPrices = [...prices];
            newPrices[idx] = { ...newPrices[idx], [priceField]: value };

            const base = newPrices[0];

            if (priceField === "currency") {
              if (idx === 0) {
                // base currency changed: recalculate the other rows
                if (base.amount > 0) {
                  const rates = await fetchRatesForBase(value);
                  for (let i = 1; i < newPrices.length; i++) {
                    if (canConvert(value, newPrices[i].currency)) {
                      newPrices[i] = {
                        ...newPrices[i],
                        amount: convertFromBase(
                          base.amount,
                          value,
                          newPrices[i].currency,
                          rates
                        ),
                      };
                    }
                  }
                }
              } else if (base.amount > 0 && canConvert(base.currency, value)) {
                const rates = await fetchRatesForBase(base.currency);
                newPrices[idx] = {
                  ...newPrices[idx],
                  amount: convertFromBase(
                    base.amount,
                    base.currency,
                    value,
                    rates
                  ),
                };
              }
            } else if (priceField === "amount" && idx === 0 && value > 0) {
              // base amount changed: recalculate the other rows
              const rates = await fetchRatesForBase(base.currency);
              for (let i = 1; i < newPrices.length; i++) {
                if (canConvert(base.currency, newPrices[i].currency)) {
                  newPrices[i] = {
                    ...newPrices[i],
                    amount: convertFromBase(
                      value,
                      base.currency,
                      newPrices[i].currency,
                      rates
                    ),
                  };
                }
              }
            }

            f.setFieldValue(field, newPrices);
          }}
          onRemove={(idx) => {
            f.setFieldValue(
              field,
              prices.filter((_, i) => i !== idx)
            );
          }}
          canRemove={prices.length > 1}
          usedCurrencies={prices.map((p) => p.currency)}
          currenciesData={currenciesData}
        />
      ))}
      <TouchableOpacity
        onPress={async () => {
          if (!currenciesData?.currencies?.length) {
            return;
          }
          const usedCurrencies = prices.map((p) => p.currency);

          // Prefer currencies that support automatic conversion
          const supportedCurrencies = currenciesData.currencies
            .filter((curr) => curr.exchangeAvailable)
            .slice(0, 6)
            .map((curr) => curr.code.toLowerCase());
          const availableCurrencies = currenciesData.currencies
            .slice(0, 10)
            .map((curr) => curr.code.toLowerCase());
          const nextCurrency =
            supportedCurrencies.find(
              (curr) => !usedCurrencies.includes(curr)
            ) ||
            availableCurrencies.find(
              (curr) => !usedCurrencies.includes(curr)
            ) ||
            "usd";

          // Auto-convert from the first row (the base price)
          let convertedAmount = 0;
          const base = prices[0];
          if (
            base &&
            base.amount > 0 &&
            canConvert(base.currency, nextCurrency)
          ) {
            const rates = await fetchRatesForBase(base.currency);
            convertedAmount = convertFromBase(
              base.amount,
              base.currency,
              nextCurrency,
              rates
            );
          }

          f.setFieldValue(field, [
            ...prices,
            { amount: convertedAmount, currency: nextCurrency },
          ]);
        }}
        style={tw`mb-4 self-start flex-row items-center px-3 py-2 rounded-lg border border-dashed border-gray-300 ${
          !currenciesData?.currencies?.length ? "opacity-50" : ""
        }`}
        disabled={!currenciesData?.currencies?.length}
        accessibilityRole="button"
      >
        <Ionicons name="add" size={16} color={colors.slate} />
        <Text
          style={[text.label, tw`ml-1 text-[13px]`, { color: colors.slate }]}
        >
          {t("Add currency")}
        </Text>
      </TouchableOpacity>
    </>
  );
};

// Compact "$20 USD" style label from a Price array (first entry)
const firstPriceLabel = (prices) => {
  const p = (prices || []).find((x) => x && x.currency);
  if (!p || !p.amount) return null;
  return `${p.amount} ${p.currency.toUpperCase()}`;
};

// Plain-language summary of everything buyers will be offered, so the
// pricing setup is never a surprise.
const BuyersPreview = ({ f, isMultiDay, days, live_only }) => {
  if (f.values.pricing_mode !== "custom_price") return null;

  const base = firstPriceLabel(f.values.custom_price);
  if (!base) return null;

  const perDayOn =
    isMultiDay &&
    !!f.values.per_day_tickets_enabled &&
    firstPriceLabel(f.values.custom_price_per_day);
  const downloadOn =
    !!f.values.sell_download_enabled &&
    firstPriceLabel(f.values.download_price);
  const replay = firstPriceLabel(f.values.replay_price) || base;

  const before = [];
  if (perDayOn) {
    before.push(
      t("Single day {price} (24h access)", {
        price: firstPriceLabel(f.values.custom_price_per_day),
      })
    );
    // Only multi-day events sell single days, so it's always "days".
    before.push(t("All {days} days {price}", { days, price: base }));
  } else if (live_only) {
    before.push(t("Livestream {price} (no replay)", { price: base }));
  } else if (isMultiDay) {
    before.push(t("Livestream {price}", { price: base }));
  } else {
    before.push(t("Livestream + Replay {price}", { price: base }));
  }
  if (downloadOn) {
    before.push(
      t("+ video download add-on {price}", {
        price: firstPriceLabel(f.values.download_price),
      })
    );
  }
  if (f.values.pass_fees_enabled) {
    before.push(t("+ service & processing fee at checkout"));
  }

  const after = [];
  if (live_only) {
    if (downloadOn) {
      after.push(
        t("Video download {price} (live-only event — no replay)", {
          price: firstPriceLabel(f.values.download_price),
        })
      );
    } else {
      after.push(t("Nothing (live-only event with downloads off)"));
    }
  } else {
    after.push(t("Replay {price}", { price: replay }));
    if (downloadOn) {
      after.push(t("Replay + download"));
    }
  }

  return (
    <Callout
      tone="neutral"
      title={t("What buyers will see")}
      style={tw`mt-4 mb-2`}
    >
      <Text style={[text.hint, tw`mt-1`]}>
        {t("Before & during the event: {items}", {
          items: before.join(" · "),
        })}
      </Text>
      <Text style={[text.hint, tw`mt-1`]}>
        {t("After the event: {items}", { items: after.join(" · ") })}
      </Text>
    </Callout>
  );
};

// Signed links to connect the Stripe account for ticket payouts, fetched from
// evt.live when Sell Tickets opens: "owner" for the Connect button, "client"
// for the copied link. Until they arrive, or if that fails, the old link is
// used; evt.live then emails the owner to confirm the account.
const useStripeConnectLinks = (event, enabled) => {
  const legacy = legacyStripeConnectLink(event);
  const [fetched, setFetched] = useState(null);
  const pending = useRef(null);

  useEffect(() => {
    if (!enabled) {
      pending.current = null;
      return undefined;
    }
    let active = true;
    pending.current = Promise.all(
      ["owner", "client"].map((purpose) =>
        fetchStripeConnectLink(event.slug, purpose).catch(() => legacy)
      )
    ).then(([owner, client]) => {
      if (active) setFetched({ legacy, owner, client });
      return { owner, client };
    });
    return () => {
      active = false;
    };
  }, [enabled, legacy, event.slug]);

  const links =
    fetched?.legacy === legacy ? fetched : { owner: legacy, client: legacy };

  // Waits for the signed link if it's still on its way (fine on iOS and
  // Android; on the web, clicks use `links` so pop-ups and copying keep the
  // click's user gesture).
  const get = useCallback(
    async (purpose) =>
      pending.current ? (await pending.current)[purpose] : legacy,
    [legacy]
  );

  return { links, get };
};

const SellTickets = ({ event }) => {
  const [stripeDetails, setStripeDetails] = useState(null);
  const [loading, setloading] = useState(false);
  const [exchangeRates, setExchangeRates] = useState({});
  const [baseCurrency, setBaseCurrency] = useState(null);
  const [baseAmount, setBaseAmount] = useState(null);
  const [currenciesData, setCurrenciesData] = useState({ currencies: [] });
  const [currenciesLoading, setCurrenciesLoading] = useState(true);
  const [currenciesError, setCurrenciesError] = useState(null);
  const clipBoardCallback = useClipboardCallback();
  const { showActionSheetWithOptions } = useActionSheet();

  const fetchPPVPricingData = async (baseCurrency = null) => {
    try {
      // Use localhost for development on web, evt.live for everything else
      const baseUrl = "https://evt.live";
      // TODO: Uncomment this when we have a local server
      // Platform.OS === "web" &&
      // (__DEV__ ||
      //   (typeof window !== "undefined" &&
      //     window.location.hostname === "localhost"))
      //   ? "http://localhost:3000"
      //   : "https://evt.live";

      const apiUrl = baseCurrency
        ? `${baseUrl}/api/ppvpricing?base_currency=${baseCurrency}`
        : `${baseUrl}/api/ppvpricing`;

      const response = await fetch(apiUrl);
      const data = await response.json();

      // Update currencies data if not already set
      if (currenciesData.currencies.length === 0) {
        setCurrenciesData({ currencies: data.currencies });
        setCurrenciesLoading(false);
        setCurrenciesError(null);
      }

      // Update exchange rates if provided
      if (data.exchangeRates && Object.keys(data.exchangeRates).length > 0) {
        setExchangeRates(data.exchangeRates);
        setBaseCurrency(data.baseCurrency);
      }

      return data.exchangeRates || {};
    } catch (error) {
      console.error("Failed to fetch PPV pricing data:", error);
      setCurrenciesError("Failed to load currencies");
      setCurrenciesLoading(false);
      return {};
    }
  };

  const fetchExchangeRates = async (currency) => {
    try {
      const data = await fetchPPVPricingData(currency);
      setBaseCurrency(currency);
      return data;
    } catch (error) {
      console.error("Failed to fetch exchange rates:", error);
      return {};
    }
  };

  const convertAmount = (amount, fromCurrency, toCurrency, rates) => {
    if (fromCurrency === toCurrency) return amount;
    const rate = rates[toCurrency.toUpperCase()];
    return rate ? Math.round(amount * rate * 100) / 100 : amount;
  };

  const getConvertedAmount = (targetCurrency, rates, amount, currency) => {
    if (!rates || !amount || amount === 0) return 0;
    return convertAmount(amount, currency, targetCurrency, rates);
  };

  const openURL = async (url) => {
    if (Platform.OS === "web") {
      window.open(url, "_blank");
    } else {
      try {
        await Linking.openURL(url);
      } catch (e) {
        // Some devices have no browser activity for https intents
        // (Sentry EVENTLIVE-IL-31G). Fall back to copying the link.
        clipBoardCallback(url);
      }
    }
  };

  const connectLinks = useStripeConnectLinks(
    event,
    !!event.slug && !event.stripe_connected_account_id
  );

  // Stripe accounts the owner's other events use, one tap each: business
  // accounts. In someone else's account evt.live answers for that account
  // (x-el-owner) to its admins, who are the only members who see this screen.
  const { account_type } = useEventAuth(event);
  const team = useTeamAccess();
  const payouts = usePayoutAccounts({
    enabled:
      account_type === "business" &&
      team.can("sellTickets", event) &&
      !!event.slug,
  });
  const hasSavedAccounts =
    !!payouts.data?.business && accountsToOffer(payouts.data).length > 0;

  const connectStripe = async () => {
    analytics.track("Connect Stripe");
    openURL(
      Platform.OS === "web"
        ? connectLinks.links.owner
        : await connectLinks.get("owner")
    );
  };

  const navigation = useNavigation();
  const { hideTicketSettings, roomsTicketing } = useRoomsTicketing(
    event,
    navigation
  );

  const unlinkStripe = () => {
    console.log("Unlink Stripe button pressed");
    showActionSheetWithOptions(
      {
        options: [t("Unlink Stripe Account"), t("Cancel")],
        cancelButtonIndex: 1,
        destructiveButtonIndex: 0,
        title: t(
          "Are you sure you want to unlink your Stripe account? This will disable ticket sales for this event."
        ),
      },
      async (buttonIndex) => {
        if (buttonIndex === 0) {
          console.log("Unlinking Stripe account...");
          try {
            await updateEventStripeDetails(event.slug, {
              stripe_connected_account_id: "",
              stripe_price_id: "",
              stripe_price_id_test: "",
              customer_chooses_price: false,
              custom_price: [],
              customer_chooses_price_default: [],
            });
            console.log("Stripe account unlinked successfully");
            // Refresh the component state
            if (Platform.OS === "web") {
              window.location.reload();
            } else {
              // For React Native, you might want to navigate back or trigger a parent component refresh
              // This depends on your navigation structure
              navigation.goBack();
            }
          } catch (err) {
            console.error("Error unlinking Stripe:", err);
            showActionSheetWithOptions(
              {
                options: [t("OK")],
                cancelButtonIndex: 0,
                title: t("Failed to unlink Stripe account. Please try again."),
              },
              () => {}
            );
          }
        }
      }
    );
  };

  const {
    stripe_price_id,
    stripe_price_id_test,
    stripe_connected_account_id,
    slug,
    broadcaster,
    customer_chooses_price,
    custom_price,
    customer_chooses_price_default,
    pricing_mode,
    custom_price_per_day,
    per_day_tickets_enabled,
    sell_download_enabled,
    download_price,
    replay_price,
    pass_fees_enabled,
    days,
    live_only,
  } = event;

  const isMultiDay = (days || 1) > 1;

  const openStripe = () => {
    openURL(
      `https://dashboard.stripe.com/b/${stripe_connected_account_id}/products`
    );
  };

  const addProduct = () => {
    openURL(
      `https://dashboard.stripe.com/b/${stripe_connected_account_id}/products?active=true&create=product&source=product_list`
    );
  };

  const fetchStripeDetails = async () => {
    if (stripe_connected_account_id) {
      setloading(true);
      try {
        const response = await fetch(
          `${eventLivePageBase}api/stripe/info?broadcaster=${broadcaster}&slug=${slug}`
        );

        const data = await response.json();
        setStripeDetails(data);
        setloading(false);
      } catch (err) {
        setloading(false);
      }
    }
  };

  useEffect(() => {
    fetchStripeDetails();
  }, [stripe_connected_account_id, broadcaster, slug]);

  // Fetch currencies data on component mount
  useEffect(() => {
    fetchPPVPricingData();
  }, []);

  // Initialize exchange rates if we have existing price data
  useEffect(() => {
    const initializeExchangeRates = async () => {
      // Only proceed if currencies data is loaded
      if (
        !currenciesData ||
        !currenciesData.currencies ||
        currenciesData.currencies.length === 0
      )
        return;

      // Check custom_price first
      if (
        custom_price &&
        custom_price.length > 0 &&
        custom_price[0].amount > 0 &&
        custom_price[0].currency
      ) {
        const firstPrice = custom_price[0];
        const currencyInfo = currenciesData.currencies.find(
          (curr) => curr.code.toLowerCase() === firstPrice.currency
        );

        if (currencyInfo?.exchangeAvailable) {
          setBaseAmount(firstPrice.amount);
          setBaseCurrency(firstPrice.currency);
          await fetchExchangeRates(firstPrice.currency);
        }
      }
      // If no custom_price, check customer_chooses_price_default
      else if (
        customer_chooses_price_default &&
        customer_chooses_price_default.length > 0 &&
        customer_chooses_price_default[0].amount > 0 &&
        customer_chooses_price_default[0].currency
      ) {
        const firstPrice = customer_chooses_price_default[0];
        const currencyInfo = currenciesData.currencies.find(
          (curr) => curr.code.toLowerCase() === firstPrice.currency
        );

        if (currencyInfo?.exchangeAvailable) {
          setBaseAmount(firstPrice.amount);
          setBaseCurrency(firstPrice.currency);
          await fetchExchangeRates(firstPrice.currency);
        }
      }
    };

    initializeExchangeRates();
  }, [
    custom_price,
    customer_chooses_price_default,
    currenciesData.currencies.length,
  ]);

  if (!event.title) {
    return <AppActivityIndicator />;
  }

  // Show loading state while currencies are being fetched
  if (currenciesLoading) {
    return (
      <SettingsPage title={t("Sell tickets")}>
        <AppActivityIndicator />
        <Text style={[text.hint, tw`text-center mt-4`]}>
          {t("Loading currencies…")}
        </Text>
      </SettingsPage>
    );
  }

  // Show error state if currencies failed to load
  if (currenciesError) {
    return (
      <SettingsPage title={t("Sell tickets")}>
        <Callout
          tone="danger"
          title={t("Couldn't load currencies")}
          action={
            <Button
              text={t("Try again")}
              icon="refresh"
              size="md"
              fullWidth={false}
              onPress={() => {
                setCurrenciesLoading(true);
                setCurrenciesError(null);
                fetchPPVPricingData();
              }}
            />
          }
        >
          {t("Check your connection and try again.")}
        </Callout>
      </SettingsPage>
    );
  }

  // A room that shares its event's tickets has none of its own to set up.
  if (hideTicketSettings) {
    return (
      <SettingsPage title={t("Sell tickets")} help="tickets.rooms">
        {roomsTicketing}
      </SettingsPage>
    );
  }

  return (
    <SettingsPage
      title={t("Sell tickets")}
      description={
        stripe_connected_account_id
          ? undefined
          : t(
              "Guests pay to watch, and the money goes straight to your Stripe account."
            )
      }
    >
      {!!roomsTicketing && (
        <SettingsSection
          first
          title={t("Tickets for rooms")}
          help="tickets.rooms"
        >
          {roomsTicketing}
        </SettingsSection>
      )}
      {!stripe_connected_account_id && (
        <SettingsSection first={!roomsTicketing}>
          <View style={tw`mb-5`}>
            {TICKET_FEATURES.map((feature) => (
              <View key={feature} style={tw`flex-row items-start mb-2`}>
                <Ionicons
                  name="checkmark-circle"
                  size={18}
                  color={colors.success}
                  style={tw`mr-2 mt-[1px]`}
                />
                <Text style={[text.body, tw`flex-1`]}>{feature}</Text>
              </View>
            ))}
          </View>
          <SavedAccountChoices event={event} payouts={payouts} />
          <ButtonStack>
            <Button
              text={
                hasSavedAccounts
                  ? t("Connect another Stripe account")
                  : t("Connect Stripe account")
              }
              icon="card-outline"
              variant={hasSavedAccounts ? "secondary" : "primary"}
              onPress={connectStripe}
            />
            <CopyButton
              value={
                Platform.OS === "web"
                  ? connectLinks.links.client
                  : () => connectLinks.get("client")
              }
              text={t("Copy link to connect Stripe")}
              copiedText={t("Link copied")}
              toast={t("Link copied")}
              toastDetail={t("Send it to the Stripe account's owner.")}
              variant="secondary"
            />
          </ButtonStack>
          <Hint>
            {t(
              "Not your Stripe account? Send the link to its owner so they can connect it."
            )}
          </Hint>
          <Hint style={tw`mt-4`}>
            {t(
              "Stripe fees, refunds and chargebacks are the connected account holder's responsibility. Need help setting up? Ask us in the support chat."
            )}
          </Hint>
        </SettingsSection>
      )}
      {stripe_connected_account_id && (
        <View>
          <SettingsSection
            first={!roomsTicketing}
            title={t("Stripe account connected")}
          >
            {stripeDetails && (
              <View
                style={[
                  tw`p-3 mb-2`,
                  {
                    borderWidth: 1,
                    borderColor: colors.border,
                    borderRadius: radius.card,
                  },
                ]}
              >
                {/* Account Details */}
                <View style={tw`flex-row items-center mb-3`}>
                  <Ionicons
                    name="checkmark-circle"
                    size={20}
                    color={colors.success}
                    style={tw`mr-2`}
                  />
                  <View style={tw`flex-1`}>
                    <Text style={text.bodyStrong}>
                      {stripeDetails.accountDetails.business_name}
                    </Text>
                    <Text style={text.hint}>
                      {stripeDetails.accountDetails.email}
                    </Text>
                  </View>
                </View>

                <ButtonRow align="start">
                  <Button
                    text={t("Stripe dashboard")}
                    icon="open-outline"
                    size="sm"
                    fullWidth={false}
                    onPress={openStripe}
                  />
                  <Button
                    text={t("Refresh")}
                    icon="refresh"
                    size="sm"
                    fullWidth={false}
                    loading={loading}
                    onPress={fetchStripeDetails}
                  />
                  <Button
                    text={t("Unlink")}
                    icon="unlink"
                    variant="danger-text"
                    size="sm"
                    fullWidth={false}
                    onPress={unlinkStripe}
                  />
                </ButtonRow>
              </View>
            )}
            {!stripeDetails && <AppActivityIndicator />}
          </SettingsSection>

          <Formik
            initialValues={{
              // Not the payout account: saving prices never writes it back,
              // so an account chosen meanwhile on another screen or device
              // isn't undone. Unlink clears it.
              stripe_price_id: stripe_price_id || "",
              stripe_price_id_test: stripe_price_id_test || "",
              pricing_mode: pricing_mode || null,
              custom_price:
                custom_price && custom_price.length > 0
                  ? custom_price
                  : [{ amount: 0, currency: "usd" }],
              customer_chooses_price_default:
                customer_chooses_price_default &&
                customer_chooses_price_default.length > 0
                  ? customer_chooses_price_default
                  : [{ amount: 0, currency: "usd" }],
              per_day_tickets_enabled: !!per_day_tickets_enabled,
              custom_price_per_day: custom_price_per_day || [],
              sell_download_enabled: !!sell_download_enabled,
              download_price: download_price || [],
              // empty = same as the ticket price
              replay_price: replay_price || [],
              pass_fees_enabled: !!pass_fees_enabled,
            }}
            onSubmit={async (values, actions) => {
              actions.setSubmitting(true);
              try {
                const submitData = { ...values };

                // Remove deprecated field
                delete submitData.customer_chooses_price;

                await updateEventStripeDetails(slug, submitData);
                actions.setSubmitting(false);
              } catch (err) {
                console.error(err);
                actions.setSubmitting(false);
                // AutoSave shows "Couldn't save · Retry".
                throw err;
              }
            }}
            validationSchema={StripeSchema}
          >
            {(f) => (
              <SettingsSection title={t("Pricing")} style={tw`mb-4`}>
                <AutoSave {...f} />

                {/* Pricing Mode Selection */}
                <FieldLabel
                  label={t("How guests pay")}
                  help="tickets.pricingMode"
                />
                <Dropdown
                  data={[
                    { label: t("Disable Pay-Per-View"), value: null },
                    {
                      label: t("Use Existing Stripe Price"),
                      value: "existing_price",
                    },
                    {
                      label: t("Custom Price (Fixed)"),
                      value: "custom_price",
                    },
                    {
                      label: t("Customer Chooses Price"),
                      value: "customer_chooses",
                    },
                  ]}
                  labelField="label"
                  valueField="value"
                  value={f.values.pricing_mode}
                  onChange={(item) =>
                    f.setFieldValue("pricing_mode", item.value)
                  }
                  style={[dropdownStyle, tw`mb-4`]}
                  selectedTextStyle={text.body}
                  itemTextStyle={text.body}
                  containerStyle={tw`p-1 border border-gray-300 rounded-lg`}
                  placeholder={t("Choose how guests pay")}
                />

                {/* Existing Stripe Price Selection */}
                {f.values.pricing_mode === "existing_price" &&
                  stripeDetails && (
                    <>
                      <Text style={[text.label, tw`mb-1.5`]}>
                        {t("Select Stripe Price")}
                      </Text>
                      <Dropdown
                        data={[
                          { name: tc("price", "None"), value: "" },
                          ...stripeDetails.prices.map((p) => ({
                            name: p.description,
                            value: p.id,
                          })),
                        ]}
                        labelField="name"
                        valueField="value"
                        value={f.values.stripe_price_id}
                        onChange={(item) =>
                          f.handleChange("stripe_price_id")(item.value)
                        }
                        style={[dropdownStyle, tw`mb-4`]}
                        selectedTextStyle={text.body}
                        itemTextStyle={text.body}
                        containerStyle={tw`p-1 border border-gray-300 rounded-lg`}
                        placeholder={t("Select Stripe Price")}
                      />
                    </>
                  )}

                {/* Custom Price Configuration */}
                {f.values.pricing_mode === "custom_price" && (
                  <>
                    <Text style={[text.label, tw`mb-1.5`]}>
                      {t("Custom Prices")}
                    </Text>
                    <Text style={[text.hint, tw`mb-3`]}>
                      {live_only
                        ? t(
                            "This event is live-only, so the ticket includes the livestream only (no replay)."
                          )
                        : isMultiDay
                        ? t("The ticket includes the livestream.")
                        : t(
                            "The ticket includes the livestream and the replay."
                          )}{" "}
                      {t(
                        "Set prices for each currency. First currency becomes the default."
                      )}
                    </Text>
                    {f.values.custom_price.map((price, index) => (
                      <PriceRow
                        key={index}
                        price={price}
                        index={index}
                        onUpdate={async (index, field, value) => {
                          const newPrices = [...f.values.custom_price];
                          newPrices[index] = {
                            ...newPrices[index],
                            [field]: value,
                          };

                          // Check if currency supports exchange rates
                          const getCurrencyInfo = (currencyCode) => {
                            if (
                              !currenciesData ||
                              !currenciesData.currencies ||
                              currenciesData.currencies.length === 0
                            ) {
                              return null;
                            }
                            return currenciesData.currencies.find(
                              (curr) => curr.code.toLowerCase() === currencyCode
                            );
                          };

                          // Handle currency changes - recalculate amount if we have exchange rates
                          if (field === "currency") {
                            const currentAmount = newPrices[index].amount;
                            const newCurrencyInfo = getCurrencyInfo(value);

                            // If the new currency doesn't support exchange rates, set amount to 0
                            if (!newCurrencyInfo?.exchangeAvailable) {
                              newPrices[index].amount = 0;
                            }
                            // If we have a base currency, exchange rates, and both currencies support exchange
                            else if (
                              baseCurrency &&
                              Object.keys(exchangeRates).length > 0 &&
                              newCurrencyInfo?.exchangeAvailable
                            ) {
                              const baseCurrencyInfo =
                                getCurrencyInfo(baseCurrency);
                              if (baseCurrencyInfo?.exchangeAvailable) {
                                newPrices[index].amount = getConvertedAmount(
                                  value,
                                  exchangeRates,
                                  baseAmount,
                                  baseCurrency
                                );
                              }
                            }

                            // If this is the first row, update base currency and recalculate all others
                            if (index === 0) {
                              const currentAmount = newPrices[0].amount;
                              const newBaseCurrencyInfo =
                                getCurrencyInfo(value);

                              if (
                                currentAmount > 0 &&
                                newBaseCurrencyInfo?.exchangeAvailable
                              ) {
                                setBaseCurrency(value);
                                const rates = await fetchExchangeRates(value);

                                // Convert all other rows that support exchange rates
                                for (let i = 1; i < newPrices.length; i++) {
                                  const targetCurrencyInfo = getCurrencyInfo(
                                    newPrices[i].currency
                                  );
                                  if (
                                    newPrices[i].currency &&
                                    newPrices[i].currency !== value &&
                                    targetCurrencyInfo?.exchangeAvailable
                                  ) {
                                    newPrices[i].amount = getConvertedAmount(
                                      newPrices[i].currency,
                                      rates,
                                      currentAmount,
                                      value
                                    );
                                  }
                                }
                              }
                            }
                            f.setFieldValue("custom_price", newPrices);
                          }
                          // Handle first row amount changes - fetch rates and convert all if supported
                          else if (
                            index === 0 &&
                            field === "amount" &&
                            value > 0
                          ) {
                            setBaseAmount(value);
                            const currentCurrency = newPrices[0].currency;
                            const baseCurrencyInfo =
                              getCurrencyInfo(currentCurrency);

                            if (baseCurrencyInfo?.exchangeAvailable) {
                              const rates = await fetchExchangeRates(
                                currentCurrency
                              );

                              // Convert all other rows that support exchange rates
                              for (let i = 1; i < newPrices.length; i++) {
                                const targetCurrencyInfo = getCurrencyInfo(
                                  newPrices[i].currency
                                );
                                if (
                                  newPrices[i].currency &&
                                  newPrices[i].currency !== currentCurrency &&
                                  targetCurrencyInfo?.exchangeAvailable
                                ) {
                                  newPrices[i].amount = getConvertedAmount(
                                    newPrices[i].currency,
                                    rates,
                                    value,
                                    currentCurrency
                                  );
                                }
                              }
                            }
                            f.setFieldValue("custom_price", newPrices);
                          } else {
                            f.setFieldValue("custom_price", newPrices);
                          }
                        }}
                        onRemove={(index) => {
                          const newPrices = f.values.custom_price.filter(
                            (_, i) => i !== index
                          );
                          f.setFieldValue("custom_price", newPrices);
                        }}
                        canRemove={f.values.custom_price.length > 1}
                        usedCurrencies={f.values.custom_price.map(
                          (p) => p.currency
                        )}
                        currenciesData={currenciesData}
                      />
                    ))}
                    <TouchableOpacity
                      onPress={() => {
                        if (
                          !currenciesData ||
                          !currenciesData.currencies ||
                          currenciesData.currencies.length === 0
                        ) {
                          return; // Don't proceed if currencies aren't loaded
                        }

                        const usedCurrencies = f.values.custom_price.map(
                          (p) => p.currency
                        );

                        // Prioritize currencies with exchange rate support
                        const supportedCurrencies = currenciesData.currencies
                          .filter((curr) => curr.exchangeAvailable)
                          .slice(0, 6) // Use top 6 supported currencies
                          .map((curr) => curr.code.toLowerCase());

                        const availableCurrencies = currenciesData.currencies
                          .slice(0, 10) // Fallback to top 10 overall
                          .map((curr) => curr.code.toLowerCase());

                        const nextCurrency =
                          supportedCurrencies.find(
                            (curr) => !usedCurrencies.includes(curr)
                          ) ||
                          availableCurrencies.find(
                            (curr) => !usedCurrencies.includes(curr)
                          ) ||
                          "usd";

                        // Calculate converted amount if we have base amount, rates, and both currencies support exchange
                        let convertedAmount = 0;
                        const nextCurrencyInfo = currenciesData.currencies.find(
                          (curr) => curr.code.toLowerCase() === nextCurrency
                        );
                        const baseCurrencyInfo = currenciesData.currencies.find(
                          (curr) => curr.code.toLowerCase() === baseCurrency
                        );

                        if (
                          baseAmount &&
                          baseCurrency &&
                          Object.keys(exchangeRates).length > 0 &&
                          nextCurrencyInfo?.exchangeAvailable &&
                          baseCurrencyInfo?.exchangeAvailable
                        ) {
                          convertedAmount = getConvertedAmount(
                            nextCurrency,
                            exchangeRates,
                            baseAmount,
                            baseCurrency
                          );
                        }

                        const newPrices = [
                          ...f.values.custom_price,
                          { amount: convertedAmount, currency: nextCurrency },
                        ];
                        f.setFieldValue("custom_price", newPrices);
                      }}
                      style={tw`mb-4 self-start flex-row items-center px-3 py-2 rounded-lg border border-dashed border-gray-300 ${
                        !currenciesData?.currencies?.length ? "opacity-50" : ""
                      }`}
                      disabled={!currenciesData?.currencies?.length}
                      accessibilityRole="button"
                    >
                      <Ionicons name="add" size={16} color={colors.slate} />
                      <Text
                        style={[
                          text.label,
                          tw`ml-1 text-[13px]`,
                          { color: colors.slate },
                        ]}
                      >
                        {t("Add currency")}
                      </Text>
                    </TouchableOpacity>

                    {/* Single-day tickets (multi-day events only) */}
                    {isMultiDay && (
                      <>
                        <ToggleRow
                          value={!!f.values.per_day_tickets_enabled}
                          onValueChange={(value) => {
                            f.setFieldValue("per_day_tickets_enabled", !!value);
                            if (
                              value &&
                              !(f.values.custom_price_per_day || []).length
                            ) {
                              f.setFieldValue("custom_price_per_day", [
                                {
                                  amount: 0,
                                  currency:
                                    f.values.custom_price?.[0]?.currency ||
                                    "usd",
                                },
                              ]);
                            }
                          }}
                          label={t("Sell single-day tickets")}
                          help="tickets.singleDay"
                        />
                        {!!f.values.per_day_tickets_enabled && (
                          <View style={tw`mt-2`}>
                            <Text style={[text.hint, tw`mb-3`]}>
                              {t(
                                "Your price above is the All-Days ticket. Set one price per day below — buyers choose which day. Single-day access lasts 24 hours from that day's scheduled start time."
                              )}
                            </Text>
                            <SimplePriceList
                              f={f}
                              field="custom_price_per_day"
                              currenciesData={currenciesData}
                            />
                          </View>
                        )}
                      </>
                    )}

                    {/* Video download add-on */}
                    <ToggleRow
                      value={!!f.values.sell_download_enabled}
                      onValueChange={(value) => {
                        f.setFieldValue("sell_download_enabled", !!value);
                        if (value && !(f.values.download_price || []).length) {
                          f.setFieldValue("download_price", [
                            {
                              amount: 0,
                              currency:
                                f.values.custom_price?.[0]?.currency || "usd",
                            },
                          ]);
                        }
                      }}
                      label={t("Sell video download")}
                      help="tickets.download"
                    />
                    {!!f.values.sell_download_enabled && (
                      <View style={tw`mt-2`}>
                        <Text style={[text.hint, tw`mb-3`]}>
                          {t(
                            "Buyers can add the downloadable video to any ticket for this amount."
                          )}{" "}
                          {live_only
                            ? t(
                                "After the event it's sold on its own since this event is live-only."
                              )
                            : t(
                                "After the event it's sold as Replay + Download."
                              )}
                          {isMultiDay
                            ? ` ${t(
                                "Single-day buyers can only download their day."
                              )}`
                            : ""}
                        </Text>
                        <SimplePriceList
                          f={f}
                          field="download_price"
                          currenciesData={currenciesData}
                        />
                      </View>
                    )}

                    {/* Optional replay price after the event */}
                    {!live_only && (
                      <>
                        {(f.values.replay_price || []).length === 0 ? (
                          <TouchableOpacity
                            onPress={() =>
                              f.setFieldValue("replay_price", [
                                {
                                  amount: 0,
                                  currency:
                                    f.values.custom_price?.[0]?.currency ||
                                    "usd",
                                },
                              ])
                            }
                            style={tw`py-3`}
                          >
                            <Text style={text.link}>
                              {t(
                                "+ Set a different replay price after the event (optional)"
                              )}
                            </Text>
                          </TouchableOpacity>
                        ) : (
                          <View style={tw`mt-2`}>
                            <View
                              style={tw`flex-row items-center justify-between mb-1`}
                            >
                              <Text style={text.label}>
                                {t("Replay price after the event")}
                              </Text>
                              <TouchableOpacity
                                onPress={() =>
                                  f.setFieldValue("replay_price", [])
                                }
                              >
                                <Text style={[text.link, tw`text-[13px]`]}>
                                  {t("Use ticket price")}
                                </Text>
                              </TouchableOpacity>
                            </View>
                            <Text style={[text.hint, tw`mb-3`]}>
                              {t(
                                "Once the event is over, new buyers pay this instead of the ticket price. You can also change any price at any time."
                              )}
                            </Text>
                            <SimplePriceList
                              f={f}
                              field="replay_price"
                              currenciesData={currenciesData}
                            />
                          </View>
                        )}
                      </>
                    )}

                    {/* Pass fees to buyers */}
                    <ToggleRow
                      value={!!f.values.pass_fees_enabled}
                      onValueChange={(value) =>
                        f.setFieldValue("pass_fees_enabled", !!value)
                      }
                      label={t("Add fees at checkout (buyers pay the fees)")}
                      help="tickets.passFees"
                    />
                    <Text style={[text.hint, tw`mt-2 mb-1`]}>
                      {f.values.pass_fees_enabled
                        ? t(
                            "Buyers pay one combined EventLive + card processing fee on top of your price, so you receive the full amount you set (card fees are estimated at the standard rate — unusual cards may vary slightly)."
                          )
                        : t(
                            "Fees are currently deducted from your price: the EventLive fee shown above plus Stripe's card processing fee."
                          )}
                    </Text>

                    <BuyersPreview
                      f={f}
                      isMultiDay={isMultiDay}
                      days={days}
                      live_only={live_only}
                    />
                  </>
                )}

                {/* Customer Chooses Price Configuration */}
                {f.values.pricing_mode === "customer_chooses" && (
                  <>
                    <Text style={[text.label, tw`mb-1.5`]}>
                      {t("Default Price Suggestions")}
                    </Text>
                    <Text style={[text.hint, tw`mb-3`]}>
                      {t(
                        "These are suggested amounts customers will see, but they can enter any amount they wish."
                      )}
                    </Text>
                    {f.values.customer_chooses_price_default.map(
                      (price, index) => (
                        <PriceRow
                          key={index}
                          price={price}
                          index={index}
                          onUpdate={async (index, field, value) => {
                            const newPrices = [
                              ...f.values.customer_chooses_price_default,
                            ];
                            newPrices[index] = {
                              ...newPrices[index],
                              [field]: value,
                            };

                            // Check if currency supports exchange rates
                            const getCurrencyInfo = (currencyCode) => {
                              if (
                                !currenciesData ||
                                !currenciesData.currencies ||
                                currenciesData.currencies.length === 0
                              ) {
                                return null;
                              }
                              return currenciesData.currencies.find(
                                (curr) =>
                                  curr.code.toLowerCase() === currencyCode
                              );
                            };

                            // Handle currency changes - recalculate amount if we have exchange rates
                            if (field === "currency") {
                              const currentAmount = newPrices[index].amount;
                              const newCurrencyInfo = getCurrencyInfo(value);

                              // If the new currency doesn't support exchange rates, set amount to 0
                              if (!newCurrencyInfo?.exchangeAvailable) {
                                newPrices[index].amount = 0;
                              }
                              // If we have a base currency, exchange rates, and both currencies support exchange
                              else if (
                                currentAmount > 0 &&
                                baseCurrency &&
                                Object.keys(exchangeRates).length > 0 &&
                                newCurrencyInfo?.exchangeAvailable
                              ) {
                                const baseCurrencyInfo =
                                  getCurrencyInfo(baseCurrency);
                                if (baseCurrencyInfo?.exchangeAvailable) {
                                  newPrices[index].amount = getConvertedAmount(
                                    value,
                                    exchangeRates,
                                    baseAmount,
                                    baseCurrency
                                  );
                                }
                              }

                              // If this is the first row, update base currency and recalculate all others
                              if (index === 0) {
                                const currentAmount = newPrices[0].amount;
                                const newBaseCurrencyInfo =
                                  getCurrencyInfo(value);

                                if (
                                  currentAmount > 0 &&
                                  newBaseCurrencyInfo?.exchangeAvailable
                                ) {
                                  setBaseCurrency(value);
                                  const rates = await fetchExchangeRates(value);

                                  // Convert all other rows that support exchange rates
                                  for (let i = 1; i < newPrices.length; i++) {
                                    const targetCurrencyInfo = getCurrencyInfo(
                                      newPrices[i].currency
                                    );
                                    if (
                                      newPrices[i].currency &&
                                      newPrices[i].currency !== value &&
                                      targetCurrencyInfo?.exchangeAvailable
                                    ) {
                                      newPrices[i].amount = getConvertedAmount(
                                        newPrices[i].currency,
                                        rates,
                                        currentAmount,
                                        value
                                      );
                                    }
                                  }
                                }
                              }
                              f.setFieldValue(
                                "customer_chooses_price_default",
                                newPrices
                              );
                            }
                            // Handle first row amount changes - fetch rates and convert all if supported
                            else if (
                              index === 0 &&
                              field === "amount" &&
                              value > 0
                            ) {
                              setBaseAmount(value);
                              const currentCurrency = newPrices[0].currency;
                              const baseCurrencyInfo =
                                getCurrencyInfo(currentCurrency);

                              if (baseCurrencyInfo?.exchangeAvailable) {
                                const rates = await fetchExchangeRates(
                                  currentCurrency
                                );

                                // Convert all other rows that support exchange rates
                                for (let i = 1; i < newPrices.length; i++) {
                                  const targetCurrencyInfo = getCurrencyInfo(
                                    newPrices[i].currency
                                  );
                                  if (
                                    newPrices[i].currency &&
                                    newPrices[i].currency !== currentCurrency &&
                                    targetCurrencyInfo?.exchangeAvailable
                                  ) {
                                    newPrices[i].amount = getConvertedAmount(
                                      newPrices[i].currency,
                                      rates,
                                      value,
                                      currentCurrency
                                    );
                                  }
                                }
                              }
                              f.setFieldValue(
                                "customer_chooses_price_default",
                                newPrices
                              );
                            } else {
                              f.setFieldValue(
                                "customer_chooses_price_default",
                                newPrices
                              );
                            }
                          }}
                          onRemove={(index) => {
                            const newPrices =
                              f.values.customer_chooses_price_default.filter(
                                (_, i) => i !== index
                              );
                            f.setFieldValue(
                              "customer_chooses_price_default",
                              newPrices
                            );
                          }}
                          canRemove={
                            f.values.customer_chooses_price_default.length > 1
                          }
                          usedCurrencies={f.values.customer_chooses_price_default.map(
                            (p) => p.currency
                          )}
                          currenciesData={currenciesData}
                        />
                      )
                    )}
                    <TouchableOpacity
                      onPress={() => {
                        if (
                          !currenciesData ||
                          !currenciesData.currencies ||
                          currenciesData.currencies.length === 0
                        ) {
                          return; // Don't proceed if currencies aren't loaded
                        }

                        const usedCurrencies =
                          f.values.customer_chooses_price_default.map(
                            (p) => p.currency
                          );

                        // Prioritize currencies with exchange rate support
                        const supportedCurrencies = currenciesData.currencies
                          .filter((curr) => curr.exchangeAvailable)
                          .slice(0, 6) // Use top 6 supported currencies
                          .map((curr) => curr.code.toLowerCase());

                        const availableCurrencies = currenciesData.currencies
                          .slice(0, 10) // Fallback to top 10 overall
                          .map((curr) => curr.code.toLowerCase());

                        const nextCurrency =
                          supportedCurrencies.find(
                            (curr) => !usedCurrencies.includes(curr)
                          ) ||
                          availableCurrencies.find(
                            (curr) => !usedCurrencies.includes(curr)
                          ) ||
                          "usd";

                        // Calculate converted amount if we have base amount, rates, and both currencies support exchange
                        let convertedAmount = 0;
                        const nextCurrencyInfo = currenciesData.currencies.find(
                          (curr) => curr.code.toLowerCase() === nextCurrency
                        );
                        const baseCurrencyInfo = currenciesData.currencies.find(
                          (curr) => curr.code.toLowerCase() === baseCurrency
                        );

                        if (
                          baseAmount &&
                          baseCurrency &&
                          Object.keys(exchangeRates).length > 0 &&
                          nextCurrencyInfo?.exchangeAvailable &&
                          baseCurrencyInfo?.exchangeAvailable
                        ) {
                          convertedAmount = getConvertedAmount(
                            nextCurrency,
                            exchangeRates,
                            baseAmount,
                            baseCurrency
                          );
                        }

                        const newPrices = [
                          ...f.values.customer_chooses_price_default,
                          { amount: convertedAmount, currency: nextCurrency },
                        ];
                        f.setFieldValue(
                          "customer_chooses_price_default",
                          newPrices
                        );
                      }}
                      style={tw`mb-4 self-start flex-row items-center px-3 py-2 rounded-lg border border-dashed border-gray-300 ${
                        !currenciesData?.currencies?.length ? "opacity-50" : ""
                      }`}
                      disabled={!currenciesData?.currencies?.length}
                      accessibilityRole="button"
                    >
                      <Ionicons name="add" size={16} color={colors.slate} />
                      <Text
                        style={[
                          text.label,
                          tw`ml-1 text-[13px]`,
                          { color: colors.slate },
                        ]}
                      >
                        {t("Add currency")}
                      </Text>
                    </TouchableOpacity>
                  </>
                )}
              </SettingsSection>
            )}
          </Formik>
          <SettingsSection title={t("More settings")}>
            <View style={tw`gap-2`}>
              <NavRow
                icon="person-circle-outline"
                label={t("Upload logo in Profile Settings")}
                onPress={() => navigation.navigate("UpdateEmail")}
              />
              <NavRow
                icon="people-outline"
                label={t("Invite free viewers in Guest List")}
                onPress={() => navigation.navigate("GuestList", { slug })}
              />
              <NavRow
                icon="brush-outline"
                label={t("Customize event branding")}
                onPress={() => navigation.navigate("EventBranding", { slug })}
              />
              <NavRow
                icon="mail-outline"
                label={t("Enable payment emails in Stripe")}
                external
                onPress={() =>
                  openURL(
                    `https://dashboard.stripe.com/b/${stripe_connected_account_id}/settings/emails`
                  )
                }
              />
              <NavRow
                icon="color-palette-outline"
                label={t("Customize branding in Stripe")}
                external
                onPress={() =>
                  openURL(
                    `https://dashboard.stripe.com/b/${stripe_connected_account_id}/settings/branding`
                  )
                }
              />
            </View>
          </SettingsSection>
        </View>
      )}
    </SettingsPage>
  );
};

export default SellTickets;
